import React, { useState, useEffect, useMemo, useLayoutEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const STORAGE_KEY = 'di-theme';

function getInitialMode(): 'light' | 'dark' {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
    if (stored === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
  } catch (e) {}

  if (
    document.documentElement.classList.contains('dark-mode') ||
    document.body.classList.contains('dark-mode')
  ) {
    return 'dark';
  }

  const darkLink = document.getElementById('theme-dark') as HTMLLinkElement | null;
  if (darkLink && darkLink.disabled === false) return 'dark';

  if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#b66dff' },
    secondary: { main: '#c3bdbd' },
    success: { main: '#1bcfb4' },
    info: { main: '#198ae3' },
    warning: { main: '#fed713' },
    error: { main: '#fe7c96' },
  },
});

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#b66dff' },
    secondary: { main: '#d8d8d8' },
    success: { main: '#1bcfb4' },
    info: { main: '#198ae3' },
    warning: { main: '#fed713' },
    error: { main: '#fe7c96' },
    background: {
      default: '#151519',
      paper: '#222328',
    },
    text: {
      primary: '#ffffff',
      secondary: '#9c9fa6',
    },
    divider: '#322f2f',
  },
});

interface ThemeWrapperProps {
  children: React.ReactNode;
}

export default function ThemeWrapper({ children }: ThemeWrapperProps) {
  const [mode, setMode] = useState<'light' | 'dark'>(getInitialMode);

  useLayoutEffect(() => {
    setMode(getInitialMode());
  }, []);

  useEffect(() => {
    function syncMode() {
      setMode(getInitialMode());
    }

    function handleThemeChange(e: Event) {
      const detail = (e as CustomEvent).detail;
      if (detail && detail.theme) {
        setMode(detail.theme === 'dark' ? 'dark' : 'light');
      } else {
        syncMode();
      }
    }

    document.addEventListener('turbolinks:load', syncMode);
    window.addEventListener('pageshow', syncMode);
    document.addEventListener('themechange', handleThemeChange);

    return () => {
      document.removeEventListener('turbolinks:load', syncMode);
      window.removeEventListener('pageshow', syncMode);
      document.removeEventListener('themechange', handleThemeChange);
    };
  }, []);

  const theme = useMemo(() => (mode === 'dark' ? darkTheme : lightTheme), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme />
      {children}
    </ThemeProvider>
  );
}
